import React from 'react';
import { Code2, Bell, Radio, Library, Layers2 } from 'lucide-react';
import './OpenClawEngineering.css';

/**
 * OpenClawEngineering — §4.
 *
 * Five vignettes: short title + one-line takeaway + real code snippet.
 * Mirrors the structure of LinearEngineering; these talk about things
 * developers actually care about when evaluating an execution layer.
 */
const VIGNETTES = [
  {
    icon: <Code2 size={18} />,
    title: 'Any-to-any execution layer',
    body: "One SDK call handles research, images, video, audio, PDFs, spreadsheets — no per-capability API design your agent has to learn. Every skill returns a unified response shape.",
    code: `result = client.create_chat(
    prompt="…",
    notify_session_key="agent:main:main",
)
# Every skill: same unified response`,
  },
  {
    icon: <Bell size={18} />,
    title: 'Notify-on-completion, not poll-until-done',
    body: "Long tasks don't block your agent. Start work with a notify_session_key; your daemon receives an event when CellCog finishes. No polling loops, no timeouts.",
    code: `client.create_chat(
    prompt="Generate a 2-page PDF…",
    notify_session_key="agent:main:pdf_task",
)
# Agent continues. Daemon handles completion.`,
  },
  {
    icon: <Radio size={18} />,
    title: 'Progress updates every 4 minutes, automatically',
    body: "Long-running work emits interim progress without you asking. Your agent can surface 'still working…' to the user without any CellCog-specific plumbing.",
    code: `# CellCog emits AGENT_UPDATE events every 4 min.
# Your daemon handler receives them for free.`,
  },
  {
    icon: <Library size={18} />,
    title: 'Auto-discovered skills catalog',
    body: 'Install once. CellCog grows new skills server-side. Your agent discovers them automatically — no breaking changes, no version bumps, no code edits.',
    code: `# A year from now, CellCog adds a new skill.
# Your agent already knows how to use it.`,
  },
  {
    icon: <Layers2 size={18} />,
    title: 'Two install paths, same key',
    body: 'ClawHub skill install for lightweight capability extension. OpenClaw plugin install for full first-class integration. Both share the same API key and capability surface.',
    code: `openclaw skills install @cellcog/cellcog
# OR
openclaw plugins install clawhub:@cellcog/openclaw-plugin`,
  },
];

const OpenClawEngineering = () => {
  return (
    <section className="openclaw-eng">
      <div className="section">
        <div className="section-eyebrow">Engineering</div>
        <h2 className="section-title display-font">
          Built for agent developers, not platform tourists.
        </h2>
        <p className="section-subtitle">
          The hard parts — long-running task orchestration, streaming
          progress, capability discovery, SDK ergonomics — handled in
          code, not in documentation.
        </p>

        <div className="openclaw-eng__grid">
          {VIGNETTES.map((v) => (
            <article key={v.title} className="openclaw-eng__vignette">
              <div className="openclaw-eng__vignette-head">
                <span className="openclaw-eng__vignette-icon">{v.icon}</span>
                <h3 className="openclaw-eng__vignette-title">{v.title}</h3>
              </div>
              <p className="openclaw-eng__vignette-body">{v.body}</p>
              <pre className="openclaw-eng__vignette-code">
                <code>{v.code}</code>
              </pre>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OpenClawEngineering;
